import { useState, useEffect } from 'react';

const THEME_KEY = 'theme';
const THEME_EVENT = 'app-theme-change';

export const getCurrentTheme = () => {
    if (typeof window === 'undefined') return 'dark';

    try {
        const saved = localStorage.getItem(THEME_KEY);

        if (saved === 'light' || saved === 'dark') {
            return saved;
        }

        return window.matchMedia('(prefers-color-scheme: light)').matches
            ? 'light'
            : 'dark';
    } catch {
        return 'dark';
    }
};

export const setAppTheme = (theme) => {
    if (theme !== 'light' && theme !== 'dark') return;

    document.documentElement.dataset.theme = theme;

    try {
        localStorage.setItem(THEME_KEY, theme);
    } catch {
        // Theme still applies if storage is unavailable.
    }

    window.dispatchEvent(new CustomEvent(THEME_EVENT, { detail: theme }));
};

export const useAppTheme = () => {
    const [theme, setTheme] = useState(getCurrentTheme);

    useEffect(() => {
        const handleThemeChange = (event) => {
            setTheme(event.detail || getCurrentTheme());
        };

        const handleStorageChange = (event) => {
            if (event.key === THEME_KEY) {
                const nextTheme = getCurrentTheme();
                document.documentElement.dataset.theme = nextTheme;
                setTheme(nextTheme);
            }
        };

        const mediaQuery = window.matchMedia('(prefers-color-scheme: light)');

        const handleSystemChange = () => {
            if (!localStorage.getItem(THEME_KEY)) {
                const nextTheme = getCurrentTheme();
                document.documentElement.dataset.theme = nextTheme;
                setTheme(nextTheme);
            }
        };

        document.documentElement.dataset.theme = getCurrentTheme();

        window.addEventListener(THEME_EVENT, handleThemeChange);
        window.addEventListener('storage', handleStorageChange);
        mediaQuery.addEventListener('change', handleSystemChange);

        return () => {
            window.removeEventListener(THEME_EVENT, handleThemeChange);
            window.removeEventListener('storage', handleStorageChange);
            mediaQuery.removeEventListener('change', handleSystemChange);
        };
    }, []);

    const toggleTheme = () => {
        const nextTheme = getCurrentTheme() === 'light' ? 'dark' : 'light';

        setAppTheme(nextTheme);
        return nextTheme;
    };

    return {
        theme,
        isLightMode: theme === 'light',
        toggleTheme,
        setTheme: setAppTheme,
    };
};

export default function DarkTheme({ style }) {
    const { isLightMode, toggleTheme } = useAppTheme();
    const [toastMessage, setToastMessage] = useState('');

    useEffect(() => {
        if (!toastMessage) return;

        const timer = setTimeout(() => {
            setToastMessage('');
        }, 2000);

        return () => clearTimeout(timer);
    }, [toastMessage]);

    const handleToggle = () => {
        const nextTheme = toggleTheme();

        setToastMessage(`Switched to ${nextTheme} mode ✔️`);
    };

    return (
        <>
            <div className="global-search-game-btn-icon" style={style}>
                <button
                    type="button"
                    onClick={handleToggle}
                    aria-label={
                        isLightMode
                            ? 'Switch to dark mode'
                            : 'Switch to light mode'
                    }
                    title={isLightMode ? 'Dark Mode' : 'Light Mode'}
                >
                    <img
                        className="light-dark-mode-icon"
                        src={
                            isLightMode
                                ? '/images/icon/FGH_ICON_SUN.webp'
                                : '/images/icon/FGH_ICON_MOON.webp'
                        }
                        alt=""
                    />
                </button>
            </div>

            {toastMessage && (
                <div className="theme-toast" role="status">
                    {toastMessage}
                </div>
            )}
        </>
    );
}
